import styled, { css } from 'styled-components'
import BoltButton from '@boltui/button'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.wrapperWidth || '100%'};
  margin-bottom: ${(props) => `${props.marginBottom}px`};
`

const Label = styled.label`
  font-weight: ${(props) => props.theme.font_weight_bold};
  margin-bottom: ${(props) => (props.hasSecondLine ? '7px' : '10px')};
`

const ErrorMessage = styled.span`
  font-size: ${(props) => props.theme.font_size_caption};
  font-weight: ${(props) => props.theme.font_weight_bold};
  color: ${(props) => props.theme.tokens.color.semantic.errorBase};
  margin-bottom: 7px;
`

const HintRow = styled.div`
  font-size: ${(props) => props.theme.font_size_caption};
  line-height: normal;
  margin-bottom: 7px;
`

const Hint = styled.span`
  font-weight: ${(props) => props.theme.font_weight};
  color: ${(props) => props.theme.tokens.color.monochrome.dark};
  margin-right: 4px;
`

const Link = styled.a`
  font-weight: ${(props) => props.theme.font_weight_bold};
  color: ${(props) => props.theme.tokens.color.palette.brand3};
  text-decoration: underline;
  white-space: nowrap;
`

const InputRow = styled.div`
  position: relative;
  height: 100%;
  max-width: ${(props) => props.maxWidth};
  flex: 1;
  -ms-flex: auto;
`

const Input = styled.input`
  width: 100%;
  margin: 1px;
  padding: 12px 17px 13px 15px;
  padding-left: ${(props) => (props.leftIcon ? `calc(${props.leftIconWidth} + 24px)` : '15px')};
  padding-right: ${(props) => (props.rightIcon ? `calc(${props.rightIconWidth} + 24px)` : '15px')};
  font-family: ${(props) => props.theme.font_family};
  font-size: ${(props) => props.theme.font_size_base};
  color: ${(props) => props.theme.tokens.color.monochrome.dark};
  background-color: ${(props) => props.theme.tokens.color.monochrome.white};
  border: ${(props) =>
    `${props.theme.tokens.modifier.border.width} solid ${props.theme.tokens.color.monochrome.medium}`};
  border-radius: ${(props) => props.theme.tokens.modifier.border.radius};

  &::placeholder {
    color: ${(props) => props.theme.tokens.color.monochrome.medium};
  }

  /* Disabled button style rules */
  ${(props) =>
    props.error &&
    css`
      border-width: ${props.theme.tokens.modifier.border.widthLarge};
      border-radius: ${props.theme.tokens.modifier.border.radiusLarge};
      border-color: ${props.theme.tokens.color.semantic.errorBase};
      color: ${props.theme.tokens.color.monochrome.dark};
    `}

  /* Disabled button style rules */
  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.35;
      cursor: not-allowed;
      background-color: ${props.theme.tokens.color.monochrome.white};
      border-color: ${props.theme.tokens.color.monochrome.medium};
      color: ${props.theme.tokens.color.monochrome.medium};
    `}

  ${(props) =>
    !props.error &&
    !props.disabled &&
    css`
      ${InputRow}:hover &,
      &:hover,
      &:active,
      &:focus {
        border-color: ${props.theme.tokens.color.monochrome.dark};
        border-width: ${props.theme.tokens.modifier.border.widthLarge};
        border-radius: ${props.theme.tokens.modifier.border.radiusLarge};
        margin: 0;
      }

      &:active,
      &:focus {
        border-color: ${props.theme.tokens.color.palette.brand3Highlight};
        color: ${props.theme.tokens.color.monochrome.darker};
        outline: none;
      }
    `}

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${(props) => props.theme.tokens.color.monochrome.white} inset !important;
  }

  ${(props) =>
    props.type === 'search'
      ? css`
          &::-webkit-search-decoration,
          &::-webkit-search-cancel-button,
          &::-webkit-search-results-button,
          &::-webkit-search-results-decoration {
            display: none;
          }
        `
      : null}

  ${(props) =>
    props.type === 'search' || props.type === 'text'
      ? css`
          &::-ms-clear,
          &::-ms-reveal {
            display: none;
            width: 0;
            height: 0;
          }
        `
      : null}
`

const IconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.position === 'left' ? '15px' : 'auto')};
  right: ${(props) => (props.position === 'left' ? 'auto' : '15px')};
  transform: translate(0, -50%);
  pointer-events: none;
`

const Button = styled(IconContainer).attrs({
  as: 'button'
})`
  background: none;
  border: none;
  padding: 0;
  pointer-events: all;
`

const ButtonFocusStyles = `
  border-top-right-radius: ${(props) => props.theme.tokens.modifier.border.widthLarge};
  border-bottom-right-radius: ${(props) => props.theme.tokens.modifier.border.widthLarge};
  margin: 0;
`

const PasswordToggle = styled.button`
  position: absolute;
  margin: 1px 0;
  top: 0;
  bottom: 0;
  right: -1px;
  width: 70px;
  background-color: ${(props) => props.theme.tokens.color.palette.brand3};
  border: transparent;
  border-top-right-radius: ${(props) => props.theme.tokens.modifier.border.width};
  border-bottom-right-radius: ${(props) => props.theme.tokens.modifier.border.width};
  font-family: ${(props) => props.theme.font_family};
  font-size: ${(props) => props.theme.font_size_base};
  font-weight: ${(props) => props.theme.font_weight_bold};
  color: ${(props) => props.theme.tokens.color.monochrome.lighter};

  /* stylelint-disable-next-line selector-type-no-unknown */
  ${InputRow}:hover & {
    ${ButtonFocusStyles}
  }

  ${(props) =>
    props.isInputFocused &&
    css`
      ${ButtonFocusStyles}
    `}
`

const CustomButton = styled(BoltButton)`
  position: absolute;
  margin: 1px 0;
  top: 0;
  bottom: 0;
  right: -1px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  /* stylelint-disable-next-line selector-type-no-unknown */
  ${InputRow}:hover & {
    ${ButtonFocusStyles}
  }

  ${(props) =>
    props.isInputFocused &&
    css`
      ${ButtonFocusStyles}
    `}
`

export {
  Wrapper,
  ErrorMessage,
  HintRow,
  Hint,
  Link,
  Label,
  InputRow,
  Input,
  IconContainer,
  Button,
  PasswordToggle,
  CustomButton
}
